import { useApolloClient, useQuery } from "@apollo/client/react";

import ORGANIZATION_RECORDING_CONSENT_EVENTS, {
  OrganizationRecordingConsentEventsData,
  OrganizationRecordingConsentEventsVariables,
  RecordingConsentEvent,
} from "../graphql/queries/OrganizationRecordingConsentEvents";

const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 100;

interface ConsentEventFilters {
  dateFrom?: string;
  dateTo?: string;
}

export function useRecordingConsentEvents(
  orgUuid: string | null,
  filters: ConsentEventFilters,
  skip = false,
) {
  const client = useApolloClient();
  const variables: OrganizationRecordingConsentEventsVariables = {
    orgUuid: orgUuid ?? "",
    ...filters,
    limit: PAGE_SIZE,
  };

  const { data, loading, error, fetchMore } = useQuery(
    ORGANIZATION_RECORDING_CONSENT_EVENTS,
    {
      variables,
      skip: skip || !orgUuid,
      notifyOnNetworkStatusChange: true,
    },
  );

  const page = data?.organizationRecordingConsentEvents;
  const events = page?.events ?? [];
  const nextCursor = page?.nextCursor ?? null;

  const loadMore = async () => {
    if (!nextCursor || loading) return;
    await fetchMore({
      variables: { ...variables, cursor: nextCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        const nextPage =
          fetchMoreResult?.organizationRecordingConsentEvents;
        if (!nextPage) return previous;
        const seen = new Set(
          previous.organizationRecordingConsentEvents.events.map(
            (event) => event.uuid,
          ),
        );
        return {
          organizationRecordingConsentEvents: {
            ...nextPage,
            events: [
              ...previous.organizationRecordingConsentEvents.events,
              ...nextPage.events.filter(
                (event) => !seen.has(event.uuid),
              ),
            ],
          },
        };
      },
    });
  };

  const getAllFilteredEvents = async (): Promise<
    RecordingConsentEvent[]
  > => {
    if (!orgUuid) return [];
    const allEvents: RecordingConsentEvent[] = [];
    let cursor: string | undefined;

    do {
      const result = await client.query<
        OrganizationRecordingConsentEventsData,
        OrganizationRecordingConsentEventsVariables
      >({
        query: ORGANIZATION_RECORDING_CONSENT_EVENTS,
        variables: {
          orgUuid,
          ...filters,
          cursor,
          limit: EXPORT_PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      });
      if (!result.data)
        throw new Error("Consent export returned no data.");
      const resultPage =
        result.data.organizationRecordingConsentEvents;
      allEvents.push(...resultPage.events);
      cursor = resultPage.nextCursor ?? undefined;
    } while (cursor);

    return allEvents;
  };

  return {
    events,
    nextCursor,
    loading,
    error,
    loadMore,
    getAllFilteredEvents,
  };
}
